import { useQuery } from "@tanstack/react-query";
import { type ReactNode, useState } from "react";
import type { ReportingTimeZone } from "@/lib/reporting-time-zone";
import { type ReportingPeriod, resolveReportingPeriod } from "@/stores/reporting-period";
import { precedingAdGroupsPeriod } from "../ad-groups/campaign-ad-groups-query";
import { campaignSearchTermsQuery } from "../targets/campaign-targets-query";
import { CampaignSearchTermsTable } from "./CampaignSearchTermsTable";

interface CampaignSearchTermsProps {
  campaignId: string;
  range: ReportingPeriod;
  timeZone: ReportingTimeZone;
  renderAdGroupLink?: (adGroupId: string, name: string) => ReactNode;
}

/** Campaign-level 搜索词 tab: every customer search term across the campaign's ad groups. */
export function CampaignSearchTerms({
  campaignId,
  range,
  timeZone,
  renderAdGroupLink,
}: CampaignSearchTermsProps) {
  const [comparison, setComparison] = useState(false);
  const dates = resolveReportingPeriod(range, new Date(), timeZone);
  const query = useQuery(
    campaignSearchTermsQuery(campaignId, { type: "custom", ...dates }, timeZone),
  );
  const previous = useQuery({
    ...campaignSearchTermsQuery(campaignId, precedingAdGroupsPeriod(dates), timeZone),
    enabled: comparison,
  });
  return (
    <CampaignSearchTermsTable
      campaignId={campaignId}
      data={query.data}
      previousData={comparison ? previous.data : undefined}
      isLoading={query.isPending}
      isFetching={query.isFetching}
      isError={query.isError}
      onRetry={() => void query.refetch()}
      comparison={comparison}
      onComparisonChange={setComparison}
      comparisonLoading={comparison && previous.isPending}
      comparisonError={comparison && previous.isError}
      onComparisonRetry={() => void previous.refetch()}
      renderAdGroupLink={renderAdGroupLink}
    />
  );
}
